import { maybe, Maybe } from '@passionware/monads';
import get from 'lodash/get';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { resolvePronounsForResourceType } from './resolvePronounsForResourceType';
import { GlobalSettingsService, PronounsOption } from './GlobalSettingsService';

function selectPronounsOptions(state: unknown): PronounsOption[] {
  const options = get(
    state,
    'global_settings.core_fields_pronouns_options',
    []
  );
  return Array.isArray(options) ? options : [];
}

function selectPronounsEnabledResourceTypeIds(state: unknown): number[] {
  const ids = get(
    state,
    'global_settings.pronouns_enabled_resource_type_ids',
    []
  );
  if (!Array.isArray(ids)) return [];
  return ids.map(id => Number(id)).filter(id => Number.isInteger(id));
}

export function createGlobalSettingsService(): GlobalSettingsService {
  return {
    usePronounsOptions() {
      return useSelector(selectPronounsOptions);
    },
    usePronounsOptionsById() {
      const options = useSelector(selectPronounsOptions);
      return useMemo(
        () => new Map(options.map(option => [option.id, option.value])),
        [options]
      );
    },
    usePronounsEnabledResourceTypeIds() {
      const ids = useSelector(selectPronounsEnabledResourceTypeIds);
      return useMemo(() => new Set(ids), [ids]);
    },
    usePronounsLabelById(optionId: Maybe<number>) {
      const options = useSelector(selectPronounsOptions);
      const pronounsById = useMemo(
        () => new Map(options.map(option => [option.id, option.value])),
        [options]
      );
      if (maybe.isAbsent(optionId)) return null;
      return pronounsById.get(optionId) ?? null;
    },
    usePronounsForResourceType(
      optionId: Maybe<number>,
      resourceTypeId: Maybe<number>
    ) {
      const pronounsEnabledResourceTypeIds = useSelector(
        selectPronounsEnabledResourceTypeIds
      );
      const options = useSelector(selectPronounsOptions);
      const pronounsById = useMemo(
        () => new Map(options.map(option => [option.id, option.value])),
        [options]
      );
      return resolvePronounsForResourceType(
        optionId,
        resourceTypeId,
        pronounsById,
        pronounsEnabledResourceTypeIds
      );
    },
    useIsPronounsEnabledForResourceType(resourceTypeId: Maybe<number>) {
      const pronounsEnabledResourceTypeIds = useSelector(
        selectPronounsEnabledResourceTypeIds
      );
      if (maybe.isAbsent(resourceTypeId)) return false;
      return pronounsEnabledResourceTypeIds.includes(resourceTypeId);
    }
  };
}
